import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem as SelectMenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Inventory,
  Payment,
  AccountBalance,
  Diamond,
  Groups,
  Receipt,
  Assessment,
  AccountCircle,
  Logout,
  TrendingUp,
  Business,
  Assignment,
  ShoppingCart,
  Settings,
  Category,
  LocalShipping,
  Description,
  BarChart,
  Build,
  ExpandMore,
  ExpandLess,
  Email,
  Search,
  Fullscreen,
  FullscreenExit,
  Close,
  Edit,
  Notifications,
  Backup,
  PersonAdd,
  Security,
  Lock,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/actions/authActions';
import WhatsAppStatusIndicator from '../WhatsAppStatusIndicator';

const drawerWidth = 240;

const menuGroups = [
  {
    title: 'Dashboard',
    items: [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }
    ]
  },
  {
    title: 'Billing',
    items: [
      { text: 'Billing', icon: <Receipt />, path: '/billing' },
      { text: 'Invoices', icon: <Description />, path: '/invoices' },
      { text: 'Invoice Payments', icon: <Payment />, path: '/invoice-payments' },
      { text: 'Invoice Number Master', icon: <Settings />, path: '/invoice-number-master' }
    ]
  },
  {
    title: 'Master Data',
    items: [
      { text: 'Customers', icon: <People />, path: '/customers' },
      { text: 'Products', icon: <Inventory />, path: '/products' },
      { text: 'Payment Modes', icon: <Payment />, path: '/payment-modes' },
      { text: 'Tax Master', icon: <AccountBalance />, path: '/tax-master' },
      { text: 'Daily Rates', icon: <TrendingUp />, path: '/daily-rates' },
      { text: 'Goldsmiths', icon: <Business />, path: '/goldsmiths' }
    ]
  },
  {
    title: 'Inventory Management',
    items: [
      { text: 'Old Metal Stock', icon: <Diamond />, path: '/old-metal-stock' },
      { text: 'Chit Groups', icon: <Groups />, path: '/chit-groups' },
      { text: 'Customer Chits', icon: <Category />, path: '/customer-chits' }
    ]
  },
  {
    title: 'Order Management',
    items: [
      { text: 'Customer Orders', icon: <Assignment />, path: '/customer-orders' },
      { text: 'Order Payments', icon: <Payment />, path: '/order-payments' },
      { text: 'Repair Orders', icon: <Build />, path: '/repair-orders' },
      { text: 'Goldsmith Purchases', icon: <LocalShipping />, path: '/goldsmith-purchases' },
      { text: 'Purchase Payments', icon: <Payment />, path: '/purchase-payments' }
    ]
  },
  {
    title: 'Communication',
    items: [
      { text: 'Communication Management', icon: <Email />, path: '/communication' }
    ]
  },
  {
    title: 'Expense Management',
    items: [
      { text: 'Expenses', icon: <Receipt />, path: '/expenses' }
    ]
  },
  {
    title: 'Reports',
    items: [
      { text: 'Invoice Reports', icon: <Assessment />, path: '/reports' },
      { text: 'Auditor Report', icon: <BarChart />, path: '/auditor-report' },
      { text: 'Analytics Reports', icon: <BarChart />, path: '/reports/analytics' },
      { text: 'Profit & Loss', icon: <Assessment />, path: '/reports/pl' }
    ]
  },
  {
    title: 'System',
    items: [
      { text: 'Backup System', icon: <Backup />, path: '/backup' }
    ]
  },
  {
    title: 'User Management',
    items: [
      { text: 'Users', icon: <PersonAdd />, path: '/users' },
      { text: 'Roles', icon: <Security />, path: '/roles' },
      { text: 'Role Permissions', icon: <Lock />, path: '/role-permissions' }
    ]
  }
];

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Sidebar should be open by default except for billing and edit invoice pages
  const isBillingPage = location.pathname === '/billing' || location.pathname.startsWith('/edit-invoice/');
  const [mobileOpen, setMobileOpen] = useState(!isBillingPage); // Open by default except for billing/edit pages
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({}); // All groups collapsed by default
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFullscreenToggle = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
    if (searchOpen) {
      setSearchQuery('');
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmLogout = () => {
    dispatch(logout());
    navigate('/login');
    setLogoutDialogOpen(false);
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleProfileClick = () => {
    setProfileDialogOpen(true);
    handleMenuClose();
  };

  const handleSettingsClick = () => {
    setSettingsDialogOpen(true);
    handleMenuClose();
  };

  // Update sidebar state when location changes
  React.useEffect(() => {
    const isBillingOrEditPage = location.pathname === '/billing' || location.pathname.startsWith('/edit-invoice/');
    if (!isMobile) {
      // On desktop, close sidebar only for billing/edit pages, open for others
      setMobileOpen(!isBillingOrEditPage);
    }
  }, [location.pathname, isMobile]);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Auto-expand group when navigating to an item within it
  const handleItemNavigation = (path, groupTitle) => {
    // Expand the group if it's collapsed
    if (!expandedGroups[groupTitle]) {
      setExpandedGroups(prev => ({
        ...prev,
        [groupTitle]: true
      }));
    }
    handleNavigation(path);
  };

  const handleGroupToggle = (groupTitle) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      <Toolbar sx={{ 
        background: 'rgba(255, 255, 255, 0.1)', 
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        minHeight: '64px !important'
      }}>
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            fontSize: '1.2rem'
          }}
        >
          JBilling
        </Typography>
      </Toolbar>
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        paddingBottom: '60px',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.5)',
        },
      }}>
        <Box sx={{ p: 1 }}>
          {menuGroups.map((group, groupIndex) => (
        <Box key={group.title}>
          <List>
            {group.title !== 'Dashboard' ? (
              <>
                <ListItemButton
                  onClick={() => handleGroupToggle(group.title)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.5,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateX(4px)',
                      transition: 'all 0.3s ease',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      flexGrow: 1,
                      textAlign: 'left',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}
                  >
                    {group.title}
                    {!expandedGroups[group.title] && group.items.length > 1 && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.6rem',
                          color: 'rgba(255, 255, 255, 0.6)',
                          ml: 1,
                          fontWeight: 'normal'
                        }}
                      >
                        ({group.items.length})
                      </Typography>
                    )}
                  </Typography>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      ml: 1,
                      color: 'white',
                      transition: 'transform 0.2s ease-in-out',
                      transform: expandedGroups[group.title] ? 'rotate(180deg)' : 'rotate(0deg)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  >
                    <ExpandMore />
                  </IconButton>
                </ListItemButton>
                <Collapse in={expandedGroups[group.title] || false} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.map((item) => (
                      <ListItem key={item.text} disablePadding>
                        <ListItemButton
                          selected={location.pathname === item.path}
                          onClick={() => handleItemNavigation(item.path, group.title)}
                          sx={{
                            ml: 2,
                            borderRadius: 2,
                            mx: 1,
                            mb: 0.5,
                            background: location.pathname === item.path 
                              ? 'rgba(255, 255, 255, 0.2)' 
                              : 'rgba(255, 255, 255, 0.05)',
                            border: location.pathname === item.path 
                              ? '1px solid rgba(255, 255, 255, 0.3)' 
                              : '1px solid transparent',
                            backdropFilter: 'blur(10px)',
                            '&.Mui-selected': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: 'white',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.25)',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.15)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              transform: 'translateX(8px)',
                              transition: 'all 0.3s ease',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <ListItemIcon sx={{ 
                            minWidth: 36,
                            color: location.pathname === item.path ? 'white' : 'rgba(255, 255, 255, 0.8)'
                          }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                              color: location.pathname === item.path ? 'white' : 'rgba(255, 255, 255, 0.9)'
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Dashboard group - always expanded
              group.items.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      background: location.pathname === item.path 
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      border: location.pathname === item.path 
                        ? '1px solid rgba(255, 255, 255, 0.3)' 
                        : '1px solid transparent',
                      backdropFilter: 'blur(10px)',
                      '&.Mui-selected': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.25)',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transform: 'translateX(8px)',
                        transition: 'all 0.3s ease',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      color: location.pathname === item.path ? 'white' : 'rgba(255, 255, 255, 0.8)'
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                        color: location.pathname === item.path ? 'white' : 'rgba(255, 255, 255, 0.9)'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
          {groupIndex < menuGroups.length - 1 && group.title !== 'Dashboard' && (
            <Divider sx={{ 
              my: 2, 
              borderColor: 'rgba(255, 255, 255, 0.1)',
              mx: 2
            }} />
          )}
          </Box>
        ))}
        </Box>
      </Box>
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        p: 2,
        background: 'rgba(0, 0, 0, 0.1)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.6)',
            textAlign: 'center',
            display: 'block',
            fontSize: '0.7rem'
          }}
        >
          JBilling v1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh',
      overflow: 'hidden'
    }}>
      <AppBar
        position="fixed"
        sx={{
          width: { 
            xs: '100%',
            md: mobileOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
          },
          ml: { 
            xs: 0,
            md: mobileOpen ? `${drawerWidth}px` : 0
          },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          transition: 'width 0.3s ease, margin-left 0.3s ease'
        }}
      >
        <Toolbar sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          minHeight: '64px !important'
        }}>
          <Tooltip title={mobileOpen ? "Close sidebar" : "Open sidebar"}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: 'block',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: 'white',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              fontSize: '1.2rem'
            }}
          >
            {menuGroups
              .flatMap(group => group.items)
              .find(item => item.path === location.pathname)?.text || 'JBilling'}
          </Typography>

          {/* WhatsApp Status Indicator */}
          <Box sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}>
            <WhatsAppStatusIndicator />
          </Box>

          {/* Search Field */}
          {searchOpen && (
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                mr: 2,
                width: 250,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleSearchToggle}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Search Toggle Button */}
          {!searchOpen && (
            <Tooltip title="Search">
              <IconButton
                color="inherit"
                onClick={handleSearchToggle}
                sx={{
                  mr: 1,
                  color: 'white',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                <Search />
              </IconButton>
            </Tooltip>
          )}

          {/* Fullscreen Toggle Button */}
          <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
            <IconButton
              color="inherit"
              onClick={handleFullscreenToggle}
              sx={{
                mr: 1,
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)'
                }
              }}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
            sx={{
              color: 'white',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }
            }}
          >
            <Avatar sx={{ 
              width: 32, 
              height: 32,
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '& .MuiSvgIcon-root': {
                color: 'white'
              }
            }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                mt: 1,
                minWidth: 280,
                overflow: 'hidden'
              }
            }}
          >
            {/* Profile Header */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    mr: 2
                  }}
                >
                  <AccountCircle sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {user?.username || 'User'}
                  </Typography>
                  <Chip
                    label={user?.role || 'User'}
                    size="small"
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Welcome back! Manage your account and preferences.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Profile Actions */}
            <Box sx={{ p: 1 }}>
              <MenuItem 
                onClick={handleProfileClick}
                sx={{
                  color: 'white',
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>

              <MenuItem 
                onClick={handleSettingsClick}
                sx={{
                  color: 'white',
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />

              <MenuItem 
                onClick={handleLogoutClick}
                sx={{
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ 
          width: { 
            xs: 0,
            md: mobileOpen ? drawerWidth : 0 
          }, 
          flexShrink: 0,
          transition: 'width 0.3s ease'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open={mobileOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: mobileOpen ? drawerWidth : 0,
              borderRight: 'none',
              boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          mt: 8,
          transition: 'width 0.3s ease',
          boxSizing: 'border-box',
          height: 'calc(100vh - 64px)', // Full height minus AppBar height
          overflowY: 'auto', // Enable vertical scrolling
          overflowX: 'hidden' // Prevent horizontal scrolling
        }}
      >
        {children}
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleCancelLogout}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Logout sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Confirm Logout
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Are you sure you want to logout?
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            You will be redirected to the login page. Any unsaved changes will be lost.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={handleCancelLogout}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            startIcon={<Logout />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 120
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <AccountCircle sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Profile Information
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                View your account details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            {/* Profile Avatar Section */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'primary.main',
                  fontSize: 48
                }}
              >
                <AccountCircle sx={{ fontSize: 80 }} />
              </Avatar>
            </Grid>

            {/* User Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={user?.username || ''}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={user?.email || 'N/A'}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role"
                value={user?.role || 'User'}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={user?.name || user?.full_name || 'N/A'}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
            </Grid>
            {user?.phone && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={user.phone}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
            )}
            {user?.created_at && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Member Since"
                  value={new Date(user.created_at).toLocaleDateString()}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={() => setProfileDialogOpen(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'secondary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Settings sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Settings
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage your application preferences
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            {/* Theme Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Appearance
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Dark Mode"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Switch between light and dark themes
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch />}
                label="Compact Mode"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Use a more compact layout
              </Typography>
            </Grid>

            {/* Notification Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Notifications
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Email Notifications"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Receive email alerts for important events
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Push Notifications"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Get browser push notifications
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Low Stock Alerts"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Get notified when stock is low
              </Typography>
            </Grid>

            {/* General Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                General
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  defaultValue="en"
                  label="Language"
                  size="small"
                >
                  <SelectMenuItem value="en">English</SelectMenuItem>
                  <SelectMenuItem value="hi">Hindi</SelectMenuItem>
                  <SelectMenuItem value="ta">Tamil</SelectMenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Date Format</InputLabel>
                <Select
                  defaultValue="dd-mm-yyyy"
                  label="Date Format"
                  size="small"
                >
                  <SelectMenuItem value="dd-mm-yyyy">DD-MM-YYYY</SelectMenuItem>
                  <SelectMenuItem value="mm-dd-yyyy">MM-DD-YYYY</SelectMenuItem>
                  <SelectMenuItem value="yyyy-mm-dd">YYYY-MM-DD</SelectMenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch />}
                label="Auto Save"
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Automatically save form data
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={() => setSettingsDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setSettingsDialogOpen(false)}
            variant="contained"
            startIcon={<Settings />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 120
            }}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
